import debounce from 'lodash.debounce';
import type { Map as MapType } from 'mapbox-gl';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import CompassIcon from '@alltrails/denali/icons/Compass';
import useMap from '../../hooks/useMap';
import type MapControlButtonOptions from '../../types/MapControlButtonOptions';
import styles from './styles/styles.module.scss';

type RotateEventHandler = (event: { type: 'rotate'; target: MapType }) => void;

type CompassButtonProps = MapControlButtonOptions & {
  alwaysVisible?: boolean;
  onClick?: () => void;
};

const CompassButton = ({ alwaysVisible = false, elevated = true, onClick, size }: CompassButtonProps) => {
  const intl = useIntl();
  const map = useMap();
  const [bearing, setBearing] = useState((map?.getBearing() || 0) * -1);

  useEffect(() => {
    const onRotate = debounce<RotateEventHandler>(({ target: updatedMap }) => {
      // Multiply by -1 since the compass is pointing at the _opposite_ of the
      // current bearing of the map.
      setBearing(updatedMap.getBearing() * -1);
    }, 100);

    map?.on('rotate', onRotate);

    return () => {
      map?.off('rotate', onRotate);
    };
  }, [map]);

  if (bearing === 0 && !alwaysVisible) {
    return null;
  }

  return (
    <IconButtonWithTooltip
      className={!size || size === 'md' || size === 'lg' ? styles.compass : undefined}
      elevated={elevated}
      icon={{ Component: CompassIcon, orientation: bearing }}
      onClick={() => {
        onClick?.();
        map?.resetNorth();
      }}
      size={size}
      title={intl.formatMessage({ defaultMessage: 'Reset north' })}
      uniqueId="map-compass-button"
      variant="ghost"
    />
  );
};

export default CompassButton;
